import { formatIsoEnZona } from '@/shared/lib/dateUtils'
import { useState, useMemo, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Revelar } from "@/shared/components/Revelar";
import { EsqueletoTabla } from "@/shared/components/Esqueleto";
import {
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  UserCircle,
  ArrowLeft,
  Download,
} from "lucide-react";
import { apiService } from '@/services/core/api.service';
import toast from "react-hot-toast";
import FilterSelect from '@/shared/components/FilterSelect';
import { categoriasApi } from '@/features/almacen/api/herramientas.api';
import { useEstadoEnUrl } from '@/shared/hooks/useEstadoEnUrl';
import { GrupoDeEvidencia } from "@/features/almacen/components/GrupoDeEvidencia";
import {
  SelloDeAceptacion,
  type DatosDelSello,
} from "@/features/almacen/components/SelloDeAceptacion";
import {
  RETORNO_POR_RECHAZO,
  accionDelMovimiento,
} from "@/features/almacen/lib/retornoPorRechazo";
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton';
import { Badge, type ColorDeBadge } from "@/shared/components/Badge";

// ─── API types ────────────────────────────────────────────────────────────────

type AccionTipo =
  | "Asignación"
  | "Reasignación"
  | "Mantenimiento"
  | "Devolución"
  | "Incidencia"
  | "Baja"
  // El retorno que provoca un rechazo (A-08): el back lo guarda como
  // reasignación o devolución, así que sin etiqueta propia no se distingue.
  | typeof RETORNO_POR_RECHAZO;

interface ApiMovimiento {
  id: string;
  toolId: string;
  toolName: string;
  toolCategory: string | null;
  type: string;
  projectId: string | null;
  projectName: string | null;
  responsibleUserId: string | null;
  responsibleUserName: string | null;
  performedByUserId: string | null;
  performedByUserName: string | null;
  resultingStatus: string;
  createdAt: string;
  evidenceUrl1?: string | null;
  evidenceUrl2?: string | null;
  evidenceUrl3?: string | null;
  /** Las que toma quien recibe al aceptar o rechazar (A-01). */
  approvalEvidenceUrl1?: string | null;
  approvalEvidenceUrl2?: string | null;
  approvalEvidenceUrl3?: string | null;
  /** El sello de la aceptación (A-02). Nulo en lo que no se aprueba. */
  approvalStatus?: 'pending' | 'approved' | 'rejected' | null;
  approvalResolvedAt?: string | null;
  approvalDeviceTimestamp?: string | null;
  approvalIp?: string | null;
  termsVersion?: string | null;
  termsVersionTitle?: string | null;
  signatureUrl?: string | null;
  rejectionReason?: string | null;
  /** El rechazo que provocó este retorno, si lo es (A-08). */
  rejectedMovementId?: string | null;
}

interface PaginatedMovimientos {
  data: ApiMovimiento[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

// ─── UI types ─────────────────────────────────────────────────────────────────

interface ResponsableRow {
  id: string;
  nombre: string;
  cargo: string;
  proyectoActual: string;
  /** Movimientos de herramienta a su nombre. 0 = no hay nada que consultar. */
  movimientos: number;
}

// GET /almacen/historial/responsables — quienes si tienen movimientos.
interface ResponsableConMovimientos {
  responsibleUserId: string;
  movements: number;
}

// Esta pantalla es para consultar movimientos, y el listado se armaba con todos
// los usuarios del sistema: la mayoria sin un solo movimiento. Por defecto se
// muestran solo los que tienen historial, y el filtro permite ver a todos.
// Estados resultantes de un movimiento, con la misma redaccion que el resto del
// modulo. Filtran quien movio una herramienta que quedo en ese estado.
const OPCIONES_ESTADO = [
  { value: "good",           label: "En buen estado" },
  { value: "new_equipment",  label: "Equipo nuevo" },
  { value: "damaged",        label: "Falla/daño" },
  { value: "missing",        label: "Faltante" },
  { value: "in_maintenance", label: "En mantenimiento" },
];

const OPCIONES_MOVIMIENTOS = [
  { value: "con", label: "Con movimientos" },
  { value: "todos", label: "Todos" },
];

// Forma real de GET /users/public/all (findAllForSelect)
interface UserSelectOption {
  id: string;
  firstName: string | null;
  lastName: string | null;
  puesto: string | null;
  lastProjectName: string | null;
}

interface MovimientoEntry {
  id: string;
  accion: AccionTipo;
  herramienta: string;
  codigoHerramienta: string;
  fechaHora: string;
  proyectoAsignado: string;
  realizadoPor: string;
  /** Fotos de quien entregó. */
  evidencias: string[];
  /** Fotos de quien recibió, que iban aparte y no se mostraban (A-01). */
  evidenciasDelReceptor: string[];
  /** Quién recibe: es el único que puede aceptar, así que es quien sella. */
  responsable: string | null;
  sello: DatosDelSello;
}

// ─── Mapping ──────────────────────────────────────────────────────────────────

function mapTipoAccion(type: string): AccionTipo {
  switch (type) {
    case "assignment":        return "Asignación";
    case "reassignment":      return "Reasignación";
    case "return":            return "Devolución";
    case "warehouse_transfer":return "Reasignación";
    case "maintenance":       return "Mantenimiento";
    case "incident":          return "Incidencia";
    case "decommission":      return "Baja";
    default:                  return "Asignación";
  }
}

function mapMovimiento(m: ApiMovimiento): MovimientoEntry {
  return {
    id:                m.id,
    accion:            accionDelMovimiento(
      mapTipoAccion(m.type),
      m.rejectedMovementId,
    ),
    herramienta:       m.toolName ?? "Sin herramienta",
    codigoHerramienta: m.toolCategory ?? "Sin categoría",
    fechaHora:         formatIsoEnZona(m.createdAt),
    proyectoAsignado:  m.projectName ?? "Sin proyecto",
    realizadoPor:      m.performedByUserName ?? "Sin registro",
    evidencias:        [m.evidenceUrl1, m.evidenceUrl2, m.evidenceUrl3].filter(
      (u): u is string => !!u,
    ),
    evidenciasDelReceptor: [
      m.approvalEvidenceUrl1,
      m.approvalEvidenceUrl2,
      m.approvalEvidenceUrl3,
    ].filter((u): u is string => !!u),
    responsable: m.responsibleUserName,
    sello: {
      approvalStatus:          m.approvalStatus,
      approvalResolvedAt:      m.approvalResolvedAt,
      approvalDeviceTimestamp: m.approvalDeviceTimestamp,
      approvalIp:              m.approvalIp,
      termsVersion:            m.termsVersion,
      termsVersionTitle:       m.termsVersionTitle,
      signatureUrl:            m.signatureUrl,
      rejectionReason:         m.rejectionReason,
    },
  };
}

const PAGE_SIZE = 12;

// ─── Acción badge ─────────────────────────────────────────────────────────────

const ACCION_COLOR: Record<AccionTipo, ColorDeBadge> = {
  Asignación: "azul",
  Reasignación: "naranja",
  Mantenimiento: "amarillo",
  Devolución: "verde",
  Incidencia: "rojo",
  // Las dos avisan de algo que salió mal: mismo rojo, la etiqueta las distingue.
  [RETORNO_POR_RECHAZO]: "rojo",
  Baja: "gris",
};

const AccionBadge = ({ value }: { value: AccionTipo }) => (
  <Badge color={ACCION_COLOR[value]}>{value}</Badge>
);

// ─── Filter dropdown ──────────────────────────────────────────────────────────

// ─── Pagination ───────────────────────────────────────────────────────────────

const Pagination = ({ page, totalPages, onPage }: { page: number; totalPages: number; onPage: (p: number) => void }) => {
  const pages = useMemo(() => {
    if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (page <= 3)       return [1, 2, 3, "...", totalPages];
    if (page >= totalPages - 2) return [1, "...", totalPages - 2, totalPages - 1, totalPages];
    return [1, "...", page - 1, page, page + 1, "...", totalPages];
  }, [page, totalPages]);

  const btn = "inline-flex items-center justify-center w-8 h-8 rounded-lg text-sm transition-colors";

  return (
    <div className="flex items-center justify-center gap-1 pt-6">
      <button onClick={() => onPage(1)} disabled={page === 1} className={`${btn} text-gray-400 hover:bg-gray-100 disabled:opacity-30`}><ChevronsLeft className="w-4 h-4" /></button>
      <button onClick={() => onPage(page - 1)} disabled={page === 1} className={`${btn} text-gray-400 hover:bg-gray-100 disabled:opacity-30`}><ChevronLeft className="w-4 h-4" /></button>
      {pages.map((p, i) =>
        p === "..." ? (
          <span key={`e-${i}`} className="w-8 h-8 inline-flex items-center justify-center text-sm text-gray-400">...</span>
        ) : (
          <button key={p} onClick={() => onPage(p as number)}
            className={`${btn} font-medium ${page === p ? "text-white" : "text-gray-600 hover:bg-gray-100"}`}
            style={page === p ? { backgroundColor: "#253579" } : undefined}
          >{p}</button>
        )
      )}
      <button onClick={() => onPage(page + 1)} disabled={page === totalPages} className={`${btn} text-gray-400 hover:bg-gray-100 disabled:opacity-30`}><ChevronRight className="w-4 h-4" /></button>
      <button onClick={() => onPage(totalPages)} disabled={page === totalPages} className={`${btn} text-gray-400 hover:bg-gray-100 disabled:opacity-30`}><ChevronsRight className="w-4 h-4" /></button>
    </div>
  );
};

// ─── Movimiento detalle ───────────────────────────────────────────────────────

const LABEL_CLASS = "block text-xs text-gray-400 mb-0.5";
const VALUE_CLASS = "text-sm text-gray-800";

/** Aquí la foto se ve en su tamaño original, no en el visor de la herramienta. */
const abrirEnOtraPestana = (url: string) =>
  window.open(url, "_blank", "noopener,noreferrer");

const MovimientoDetalle = ({
  entry,
  responsable,
  onBack,
}: {
  entry: MovimientoEntry;
  responsable: ResponsableRow;
  onBack: () => void;
}) => (
  <div className="mx-auto px-8 py-8">
    <div className="flex items-start gap-3 mb-6">
      <button type="button" onClick={onBack} className={estiloDeBoton('secundario', 'mt-1.5')}>
        <ArrowLeft className="w-4 h-4" />
        Volver
      </button>
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{responsable.nombre}</h1>
        <p className="text-sm text-gray-500 mt-0.5">Detalle de movimiento</p>
      </div>
    </div>

    <div className="bg-white rounded-xl border shadow-sm mb-5" style={{ borderColor: "#E0E0E0" }}>
      <div className="px-6 py-4 border-b" style={{ borderColor: "#E0E0E0" }}>
        <h2 className="text-base font-semibold text-gray-800">Información general</h2>
      </div>
      <div className="p-6">
        <div className="grid grid-cols-4 gap-5">
          <div>
            <p className={LABEL_CLASS}>Acción</p>
            <p className={VALUE_CLASS}>{entry.accion}</p>
          </div>
          <div>
            <p className={LABEL_CLASS}>Herramienta</p>
            <p className={VALUE_CLASS}>{entry.herramienta}</p>
          </div>
          <div>
            <p className={LABEL_CLASS}>Fecha y hora</p>
            <p className={VALUE_CLASS}>{entry.fechaHora}</p>
          </div>
          <div>
            <p className={LABEL_CLASS}>Proyecto</p>
            <p className={VALUE_CLASS}>{entry.proyectoAsignado}</p>
          </div>
          <div>
            <p className={LABEL_CLASS}>Realizado por</p>
            <p className={VALUE_CLASS}>{entry.realizadoPor}</p>
          </div>
        </div>
      </div>
    </div>

    {/* Dentro del movimiento que sella: aceptar no mueve la herramienta (A-02). */}
    <SelloDeAceptacion sello={entry.sello} receptor={entry.responsable} className="mb-5" />

    <div className="bg-white rounded-xl border shadow-sm" style={{ borderColor: "#E0E0E0" }}>
      <div className="px-6 py-4 border-b" style={{ borderColor: "#E0E0E0" }}>
        <h2 className="text-base font-semibold text-gray-800">Evidencia fotográfica</h2>
      </div>
      <div className="p-6">
        {entry.evidencias.length === 0 &&
        entry.evidenciasDelReceptor.length === 0 ? (
          <p className="text-sm text-gray-400 italic">Sin evidencia adjunta</p>
        ) : (
          <div className="space-y-6">
            <GrupoDeEvidencia
              titulo="Fotos de quien entrega"
              urls={entry.evidencias}
              onVer={abrirEnOtraPestana}
            />

            {entry.evidenciasDelReceptor.length > 0 && (
              <GrupoDeEvidencia
                titulo="Fotos de quien recibe"
                urls={entry.evidenciasDelReceptor}
                onVer={abrirEnOtraPestana}
              />
            )}
          </div>
        )}
      </div>
    </div>
  </div>
);

// ─── Responsable detalle (historial de movimientos) ───────────────────────────

const ResponsableDetalle = ({
  responsable,
  onBack,
}: {
  responsable: ResponsableRow;
  onBack: () => void;
}) => {
  const [movimientos, setMovimientos]    = useState<MovimientoEntry[]>([]);
  const [loading, setLoading]            = useState(true);
  const [search, setSearch]              = useState("");
  const [filterProyecto, setFilterProyecto]   = useState("");
  const [filterHerramienta, setFilterHerramienta] = useState("");
  const [filterAccion, setFilterAccion]  = useState("");
  const [selectedMovimiento, setSelectedMovimiento] = useState<MovimientoEntry | null>(null);

  useEffect(() => {
    const fetchHistorial = async () => {
      setLoading(true);
      try {
        const res = await apiService.request<PaginatedMovimientos>(
          `/almacen/historial/responsable?responsibleUserId=${responsable.id}&limit=100`
        );
        const raw = res.data ?? [];
        setMovimientos(raw.map(mapMovimiento));
      } catch (err: unknown) {
        const e = err as { message?: string | string[] };
        const msg = Array.isArray(e.message) ? e.message.join(" ") : (e.message ?? "Error al cargar historial");
        toast.error(msg);
      } finally {
        setLoading(false);
      }
    };
    fetchHistorial();
  }, [responsable.id]);

  const ACCIONES: AccionTipo[] = ["Asignación", "Reasignación", "Mantenimiento", "Devolución", "Incidencia", "Baja"];
  const proyectos    = useMemo(() => [...new Set(movimientos.map((r) => r.proyectoAsignado).filter((v) => v !== "Sin proyecto"))], [movimientos]);
  const herramientas = useMemo(() => [...new Set(movimientos.map((r) => r.herramienta).filter((v) => v !== "Sin herramienta"))], [movimientos]);

  const rows = movimientos.filter((r) => {
    const q = search.toLowerCase();
    const matchSearch = !q || r.herramienta.toLowerCase().includes(q) || r.proyectoAsignado.toLowerCase().includes(q);
    return (
      matchSearch &&
      (!filterProyecto    || r.proyectoAsignado === filterProyecto) &&
      (!filterHerramienta || r.herramienta      === filterHerramienta) &&
      (!filterAccion      || r.accion           === filterAccion)
    );
  });

  // Early return AFTER all hooks
  if (selectedMovimiento) {
    return (
      <MovimientoDetalle
        entry={selectedMovimiento}
        responsable={responsable}
        onBack={() => setSelectedMovimiento(null)}
      />
    );
  }

  return (
    <div className="mx-auto px-8 py-8">
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-start gap-3">
          <button type="button" onClick={onBack} className={estiloDeBoton('secundario', 'mt-1.5')}>
            <ArrowLeft className="w-4 h-4" />
            Volver
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Responsables</h1>
            <p className="text-sm text-gray-500 mt-0.5">Consulta los movimientos e historial de herramientas por trabajador</p>
          </div>
        </div>
        <button type="button" className={estiloDeBoton('primario')}>
          <Download className="elevar w-4 h-4" />
          Descargar
        </button>
      </div>

      {/* Filter bar */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-3 flex flex-wrap items-center gap-3 mb-6">
        <div className="relative w-[260px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#3B5998]"
          />
        </div>
        <FilterSelect label="Proyecto asignado" options={(proyectos).map((o) => ({ value: o, label: o }))}     value={filterProyecto}    onChange={setFilterProyecto}    ancho="contenido"
          />
        <FilterSelect label="Herramienta"        options={(herramientas).map((o) => ({ value: o, label: o }))}  value={filterHerramienta} onChange={setFilterHerramienta} ancho="contenido"
          />
        <FilterSelect label="Acción"             options={(ACCIONES).map((o) => ({ value: o, label: o }))}     value={filterAccion}      onChange={setFilterAccion}      ancho="contenido"
          />
        <button
          onClick={() => { setSearch(""); setFilterProyecto(""); setFilterHerramienta(""); setFilterAccion(""); }}
          className="text-sm font-semibold text-[#315FB9] hover:underline"
        >
          Limpiar
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr style={{ backgroundColor: "#FAF9FF", borderBottom: "1px solid #E4E4E4" }}>
                {["Acción", "Herramienta", "Fecha y hora", "Realizado por", "Proyecto asignado"].map((h) => (
                  <th key={h} className="text-left py-3 px-4 text-sm font-semibold first:pl-6 last:pr-6 whitespace-nowrap" style={{ color: "#534D59" }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                // A-01: la silueta va dentro de la tabla para no perder el ancho
                // de las columnas mientras cargan los datos.
                <tr>
                  <td colSpan={5} className="p-0">
                    <EsqueletoTabla filas={6} columnas={5} />
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={5}>
                    <div className="flex flex-col items-center justify-center py-16 gap-2">
                      <p className="text-sm font-medium text-gray-500">Sin resultados</p>
                      <p className="text-sm text-gray-400">No hay movimientos registrados.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr
                    key={row.id}
                    onClick={() => setSelectedMovimiento(row)}
                    className="hover:bg-[#F4F7FB] transition-colors cursor-pointer"
                    style={{ borderBottom: "1px solid #E4E4E4" }}
                  >
                    <td className="py-3 px-4 pl-6 whitespace-nowrap">
                      <AccionBadge value={row.accion} />
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap">
                      <p className="text-gray-800 font-medium">{row.herramienta}</p>
                      <p className="text-xs text-gray-400">{row.codigoHerramienta}</p>
                    </td>
                    <td className="py-3 px-4 text-gray-600 whitespace-nowrap">{row.fechaHora}</td>
                    <td className="py-3 px-4 text-gray-600 whitespace-nowrap">{row.realizadoPor}</td>
                    <td className="py-3 px-4 pr-6 text-gray-600 whitespace-nowrap">{row.proyectoAsignado}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const HistorialResponsable = () => {
  const [responsables, setResponsables]           = useState<ResponsableRow[]>([]);
  const [loading, setLoading]                     = useState(true);
  // Filtros y pagina en la URL y no en useState: al volver del detalle la
  // pantalla se monta de nuevo, y en memoria se perdia todo lo aplicado.
  const [search, setSearch]                       = useEstadoEnUrl<string>("buscar", "");
  const [filterCategoria, setFilterCategoria]     = useEstadoEnUrl<string>("categoria", "");
  const [filterProyecto, setFilterProyecto]       = useEstadoEnUrl<string>("proyecto", "");
  const [filterResponsable, setFilterResponsable] = useEstadoEnUrl<string>("responsable", "");
  const [filterEstado, setFilterEstado]           = useEstadoEnUrl<string>("estado", "");
  const [filterMovimientos, setFilterMovimientos] = useEstadoEnUrl<string>("movimientos", "con");
  const [categorias, setCategorias]               = useState<{ value: string; label: string }[]>([]);
  const [page, setPage]                           = useEstadoEnUrl<number>("pagina", 1);
  // El responsable abierto sale de la URL, no del estado: antes el detalle y el
  // listado compartian direccion, asi que volver no podia regresar a donde
  // estaba el usuario ni se aguantaba un refresh.
  const location = useLocation();
  const navigate = useNavigate();
  const { responsableId } = useParams();
  const responsableDelState =
    (location.state as { preselectResponsable?: ResponsableRow } | null)?.preselectResponsable ?? null;

  // ── Catalogo de categorias para el filtro ──
  useEffect(() => {
    categoriasApi
      .findAll()
      .then((lista) => setCategorias(lista.map((c) => ({ value: c.id, label: c.name }))))
      // Sin catalogo el filtro queda vacio, que es lo mismo que tener antes: no
      // vale la pena molestar con un toast por un filtro.
      .catch(() => setCategorias([]));
  }, []);

  // ── Fetch users ──
  // Categoria y estado se resuelven en el servidor: son atributos de los
  // movimientos, no del usuario, asi que la lista se vuelve a pedir al cambiarlos.
  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (filterCategoria) params.set("categoryId", filterCategoria);
        if (filterEstado) params.set("resultingStatus", filterEstado);
        const consulta = params.toString();

        // Si la consulta de movimientos falla, se sigue con el listado completo:
        // es mejor mostrar a todos que dejar la pantalla vacia.
        const [users, conMovimientos] = await Promise.all([
          apiService.request<UserSelectOption[]>("/users/public/all"),
          apiService
            .request<ResponsableConMovimientos[]>(
              `/almacen/historial/responsables${consulta ? `?${consulta}` : ""}`,
            )
            .catch(() => [] as ResponsableConMovimientos[]),
        ]);
        const movimientosPorUsuario = new Map(
          conMovimientos.map((r) => [r.responsibleUserId, r.movements])
        );
        setResponsables(
          users.map((u) => ({
            id:             u.id,
            nombre:         `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim(),
            cargo:          u.puesto ?? "Sin puesto",
            proyectoActual: u.lastProjectName ?? "Sin proyecto",
            movimientos:    movimientosPorUsuario.get(u.id) ?? 0,
          }))
        );
      } catch (err: unknown) {
        const e = err as { message?: string | string[] };
        const msg = Array.isArray(e.message) ? e.message.join(" ") : (e.message ?? "Error al cargar usuarios");
        toast.error(msg);
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, [filterCategoria, filterEstado]);

  const handleSearch = (v: string) => { setSearch(v); setPage(1); };

  // Base del listado segun el filtro de movimientos. De aqui salen tambien las
  // opciones del filtro por responsable: ofrecer nombres que el listado no puede
  // mostrar solo produce busquedas sin resultados.
  const base = useMemo(() => {
    // Filtrar por categoria o por estado ya es filtrar movimientos: alguien sin
    // ninguno no puede cumplirlos. Con esos filtros puestos, "Todos" no puede
    // devolver a quien no tiene movimientos, o el filtro no serviria de nada.
    const filtraMovimientos = Boolean(filterCategoria || filterEstado);
    return filterMovimientos === "todos" && !filtraMovimientos
      ? responsables
      : responsables.filter((r) => r.movimientos > 0);
  }, [responsables, filterMovimientos, filterCategoria, filterEstado]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return base.filter((r) => {
      const matchSearch = !q || r.nombre.toLowerCase().includes(q) || r.cargo.toLowerCase().includes(q) || r.proyectoActual.toLowerCase().includes(q);
      return (
        matchSearch &&
        // Proyecto actual filtra la columna que se ve —el proyecto en el que esta
        // hoy la persona—, no el proyecto de sus movimientos viejos: si no,
        // saldrian filas cuya columna dice otra cosa.
        (!filterProyecto || r.proyectoActual === filterProyecto) &&
        (!filterResponsable || r.nombre === filterResponsable)
      );
    });
  }, [search, filterProyecto, filterResponsable, base]);

  const uniqueNombres = useMemo(() => base.map((r) => r.nombre), [base]);
  const uniqueProyectos = useMemo(
    () => [...new Set(base.map((r) => r.proyectoActual).filter(Boolean))],
    [base],
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage   = Math.min(page, totalPages);
  const pageItems  = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  // Early return AFTER all hooks
  const selectedResponsable = responsableId
    ? (responsableDelState?.id === responsableId
        ? responsableDelState
        : responsables.find((r) => r.id === responsableId) ?? null)
    : null;

  // Volver regresa por el historial del navegador; si la pantalla se abrio
  // directo por URL no hay a donde volver y se va al listado.
  const volverDelDetalle = () => {
    if (location.key !== "default") navigate(-1);
    else navigate("/almacen/historial/responsable");
  };

  if (responsableId) {
    if (!selectedResponsable) {
      return (
        <div className="mx-auto px-8 py-8">
          <p className="text-sm text-gray-500">
            {loading ? "Cargando responsable…" : "No se encontró el responsable."}
          </p>
        </div>
      );
    }
    return (
      <ResponsableDetalle
        responsable={selectedResponsable}
        onBack={volverDelDetalle}
      />
    );
  }

  return (
    <div className="mx-auto px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Responsables</h1>
        <p className="text-sm text-gray-500 mt-1">
          Consulta los movimientos e historial de herramientas por trabajador
        </p>
      </div>

      {/* Filter bar */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-3 flex flex-wrap items-center gap-3 mb-6">
        <div className="relative w-[260px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar en responsables..."
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-lg text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#3B5998]"
          />
        </div>
        <FilterSelect label="Categoría"         options={categorias}                                          value={filterCategoria}   onChange={(v) => { setFilterCategoria(v);   setPage(1); }} ancho="contenido"
          />
        <FilterSelect label="Proyecto actual"   options={(uniqueProyectos).map((o) => ({ value: o, label: o }))} value={filterProyecto}   onChange={(v) => { setFilterProyecto(v);    setPage(1); }} ancho="contenido"
          />
        <FilterSelect label="Responsable"       options={(uniqueNombres).map((o) => ({ value: o, label: o }))} value={filterResponsable} onChange={(v) => { setFilterResponsable(v); setPage(1); }} ancho="contenido"
          />
        <FilterSelect label="Estado"            options={OPCIONES_ESTADO}                                     value={filterEstado}      onChange={(v) => { setFilterEstado(v);      setPage(1); }} ancho="contenido"
          />
        <FilterSelect label="Movimientos"       options={OPCIONES_MOVIMIENTOS} allLabel={null}                               value={filterMovimientos} onChange={(v) => { setFilterMovimientos(v || "con"); setPage(1); }} ancho="contenido"
          />
        <button
          onClick={() => { setSearch(""); setFilterCategoria(""); setFilterProyecto(""); setFilterResponsable(""); setFilterEstado(""); setFilterMovimientos("con"); setPage(1); }}
          className="text-sm font-semibold text-[#315FB9] hover:underline"
        >
          Limpiar
        </button>
      </div>

      {/* Table */}
      <Revelar>
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr style={{ backgroundColor: "#FAF9FF", borderBottom: "1px solid #E4E4E4" }}>
                {["Responsable", "Puesto", "Proyecto actual"].map((h, i) => (
                  <th
                    key={`${h}-${i}`}
                    className="text-left py-3 px-4 text-sm font-semibold first:pl-6 last:pr-6 whitespace-nowrap"
                    style={{ color: "#534D59" }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                // A-01: la silueta va dentro de la tabla para no perder el ancho
                // de las columnas mientras cargan los datos.
                <tr>
                  <td colSpan={3} className="p-0">
                    <EsqueletoTabla filas={6} columnas={3} />
                  </td>
                </tr>
              ) : pageItems.length === 0 ? (
                <tr>
                  <td colSpan={3}>
                    <div className="flex flex-col items-center justify-center py-16 gap-2">
                      <UserCircle className="w-10 h-10 text-gray-300" />
                      <p className="text-sm font-medium text-gray-500">Sin resultados</p>
                      <p className="text-sm text-gray-400">
                        {filterMovimientos === "con"
                          ? "Nadie tiene movimientos de herramienta todavía. Cambia el filtro a Todos para ver al resto."
                          : "Intenta con otros filtros."}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                pageItems.map((row) => (
                  <tr
                    key={row.id}
                    onClick={() =>
                      navigate(`/almacen/historial/responsable/${row.id}`, {
                        state: { preselectResponsable: row },
                      })
                    }
                    className="hover:bg-[#F4F7FB] transition-colors cursor-pointer"
                    style={{ borderBottom: "1px solid #E4E4E4" }}
                  >
                    <td className="py-3 px-4 pl-6 whitespace-nowrap">
                      <p className="text-gray-800 font-medium">{row.nombre || "Sin responsable"}</p>
                    </td>
                    <td className="py-3 px-4 text-gray-600 whitespace-nowrap">
                      {row.cargo || "Sin puesto"}
                    </td>
                    <td className="py-3 px-4 pr-6 text-gray-600 whitespace-nowrap">
                      {row.proyectoActual || "Sin proyecto"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      </Revelar>

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination page={safePage} totalPages={totalPages} onPage={setPage} />
      )}
    </div>
  );
};

export default HistorialResponsable;
